// Opportunity (issue) details slide-over. Tabs: Overview / Activity.
// Overview has vote button + status switch + convert-to-rock /
// convert-to-todo, raiser block, home-team chip, story body. Activity
// is the comment thread.

import { useState } from "react";
import {
  Archive,
  ChevronUp,
  CheckSquare,
  Lightbulb,
  Target,
  X,
} from "lucide-react";
import { tpApi } from "../api";
import { useResource } from "../hooks";
import {
  currentQuarter,
  formatDateShort,
  nextQuarter,
  priorityChipClass,
  statusChipClass,
} from "../utils";
import { Avatar } from "../components/Avatar";
import { ActivityFeed } from "../components/ActivityFeed";
import {
  InlineEditableText,
  InlineEditableMultiline,
  InlineEditableRichText,
} from "../components/InlineEditable";
import { KebabMenu } from "../components/KebabMenu";
import { DC } from "./createSlideOver/primitives";
import { ISSUE_STATUS } from "../../../../../../shared/team-planner/schemas";

type IssueTab = "overview" | "activity";

interface IssueSheetProps {
  id: string;
  tab?: string;
  onClose: () => void;
  onChanged: () => void;
}

export function IssueSheet({ id, onClose, onChanged }: IssueSheetProps) {
  const issueRes = useResource(() => tpApi.getIssue(id), [id]);
  const issue = issueRes.data;
  const [tab, setTab] = useState<IssueTab>("overview");
  const [busy, setBusy] = useState(false);
  const [convertOpen, setConvertOpen] = useState<null | "todo" | "rock">(null);

  async function vote() {
    if (!issue) return;
    await tpApi.voteIssue(issue.id, issue.viewerHasVoted ? "unvote" : "vote");
    issueRes.refresh();
    onChanged();
  }
  async function setStatus(s: string) {
    setBusy(true);
    try {
      await tpApi.setIssueStatus(id, s);
      issueRes.refresh();
      onChanged();
    } finally {
      setBusy(false);
    }
  }
  async function patch(input: Record<string, unknown>) {
    setBusy(true);
    try {
      await tpApi.updateIssue(id, input);
      issueRes.refresh();
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="tp-sheet-backdrop" onClick={onClose} aria-hidden />
      <aside className="tp-sheet" role="dialog" aria-modal="true">
        <div style={{ height: 4, background: "var(--bsp-orange)", flex: "0 0 4px" }} />
        <header className="tp-sheet-header" style={{ flexDirection: "column", alignItems: "stretch", paddingBottom: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: "var(--bsp-orange-tint)",
                color: "var(--bsp-orange)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Lightbulb size={18} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  font: "700 10px/1 var(--bsp-font-sans)",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: DC.grey,
                  marginBottom: 3,
                }}
              >
                Opportunity · O{issue?.seq ?? "—"}
              </div>
              {issue ? (
                <InlineEditableText
                  value={issue.title}
                  required
                  maxLength={300}
                  readStyle={{
                    font: "700 16px/1.2 var(--bsp-font-display)",
                    letterSpacing: "0.01em",
                    color: DC.dark,
                    textTransform: "uppercase",
                  }}
                  onCommit={(next) => patch({ title: next })}
                />
              ) : (
                <div style={{ font: "700 16px/1.2 var(--bsp-font-display)", color: DC.dark }}>
                  Loading…
                </div>
              )}
            </div>
            {issue ? (
              <KebabMenu
                actions={[
                  {
                    label: "Mark actioned",
                    icon: <CheckSquare size={14} />,
                    disabled: issue.status === "actioned",
                    onClick: () => setStatus("actioned"),
                  },
                  { label: "_divider", divider: true, onClick: () => undefined },
                  {
                    label: "Archive opportunity",
                    icon: <Archive size={14} />,
                    danger: true,
                    onClick: async () => {
                      if (!window.confirm("Archive this opportunity?")) return;
                      await tpApi.archiveIssue(issue.id);
                      onChanged();
                      onClose();
                    },
                  },
                ]}
              />
            ) : null}
            <button type="button" className="tp-icon-action" onClick={onClose} aria-label="Close">
              <X size={16} />
            </button>
          </div>
          {issue ? (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
              <button
                type="button"
                onClick={vote}
                className={`bsp-btn bsp-btn-sm ${issue.viewerHasVoted ? "bsp-btn-primary" : "bsp-btn-secondary"}`}
              >
                <ChevronUp size={12} /> {issue.voteCount}{" "}
                {issue.voteCount === 1 ? "vote" : "votes"}
              </button>
              <span className={statusChipClass(issue.status)}>{issue.status}</span>
              <span className={priorityChipClass(issue.priority)}>{issue.priority}</span>
              <span className="bsp-chip bsp-chip-grey">{issue.term.replace("_", " ")}</span>
              {issue.homeTeam ? (
                <span className="bsp-chip bsp-chip-blue">{issue.homeTeam.name}</span>
              ) : null}
            </div>
          ) : null}
        </header>

        <nav
          role="tablist"
          aria-label="Opportunity details sections"
          style={{
            display: "flex",
            alignItems: "center",
            padding: "0 22px",
            borderBottom: `1px solid ${DC.border}`,
            flexShrink: 0,
            background: DC.surface,
          }}
        >
          {(["overview", "activity"] as IssueTab[]).map((t) => {
            const active = tab === t;
            const label = t === "overview" ? "Overview" : "Activity";
            return (
              <button
                key={t}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(t)}
                style={{
                  padding: "11px 12px",
                  border: 0,
                  background: "transparent",
                  font: "700 11px/1 var(--bsp-font-sans)",
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  color: active ? "var(--bsp-orange-fg)" : DC.grey,
                  borderBottom: `2px solid ${active ? "var(--bsp-orange)" : "transparent"}`,
                  marginBottom: -1,
                  cursor: "pointer",
                }}
              >
                {label}
              </button>
            );
          })}
        </nav>

        <div style={{ flex: 1, overflowY: "auto", padding: "16px 22px 22px" }}>
          {!issue ? (
            <p style={{ color: DC.grey }}>Loading…</p>
          ) : tab === "overview" ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <Block title="Status">
                <select
                  className="bsp-select"
                  value={issue.status}
                  disabled={busy}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  {ISSUE_STATUS.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </Block>

              <Block title="Raised by">
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  {issue.isAnonymous ? (
                    <span style={{ color: DC.grey, fontStyle: "italic" }}>Anonymous</span>
                  ) : issue.raisedBy ? (
                    <>
                      <Avatar owner={issue.raisedBy} />
                      <span>{issue.raisedBy.displayName}</span>
                    </>
                  ) : (
                    <span style={{ color: DC.grey }}>Unknown</span>
                  )}
                  <span style={{ marginLeft: "auto", color: DC.grey, fontSize: 11 }}>
                    {formatDateShort(issue.raisedAt)}
                  </span>
                </div>
              </Block>

              <Block title="Headline">
                <InlineEditableText
                  value={issue.shortDescription ?? ""}
                  maxLength={280}
                  placeholder="Click to add a one-line spotter call…"
                  readStyle={{ fontSize: 13, fontWeight: 600, lineHeight: 1.4 }}
                  onCommit={(next) =>
                    patch({ shortDescription: next.trim() === "" ? null : next })
                  }
                />
              </Block>

              <Block title="Story">
                <InlineEditableRichText
                  value={issue.story ?? ""}
                  rows={4}
                  placeholder="Click to add the full story — problem, idea, context…"
                  onCommit={(next) =>
                    patch({ story: next.trim() === "" ? null : next })
                  }
                />
              </Block>

              {issue.status !== "converted" && issue.status !== "archived" ? (
                <Block title="Convert">
                  <div style={{ display: "flex", gap: 8 }}>
                    <button
                      type="button"
                      className="bsp-btn bsp-btn-secondary bsp-btn-sm"
                      onClick={() => setConvertOpen("todo")}
                    >
                      <CheckSquare size={12} /> To a to-do
                    </button>
                    <button
                      type="button"
                      className="bsp-btn bsp-btn-secondary bsp-btn-sm"
                      onClick={() => setConvertOpen("rock")}
                    >
                      <Target size={12} /> To a rock
                    </button>
                  </div>
                </Block>
              ) : null}

              {issue.resolutionNotes ? (
                <Block title="Resolution notes">
                  <div style={{ fontSize: 13, lineHeight: 1.5 }}>{issue.resolutionNotes}</div>
                </Block>
              ) : null}
            </div>
          ) : (
            <ActivityFeed itemKind="issue" itemId={id} />
          )}
        </div>

        {convertOpen ? (
          <ConvertDialog
            issueId={id}
            mode={convertOpen}
            onClose={() => setConvertOpen(null)}
            onConverted={() => {
              setConvertOpen(null);
              onChanged();
              onClose();
            }}
          />
        ) : null}
      </aside>
    </>
  );
}

function ConvertDialog({
  issueId,
  mode,
  onClose,
  onConverted,
}: {
  issueId: string;
  mode: "todo" | "rock";
  onClose: () => void;
  onConverted: () => void;
}) {
  const [quarter, setQuarter] = useState(nextQuarter(currentQuarter()));
  const defaultDue = (() => {
    const d = new Date();
    d.setMonth(d.getMonth() + 2);
    return d.toISOString().slice(0, 10);
  })();
  const [due, setDue] = useState(defaultDue);
  const [pending, setPending] = useState(false);

  async function go() {
    setPending(true);
    try {
      if (mode === "rock") {
        await tpApi.convertIssueToRock(issueId, {
          quarter,
          dueAt: new Date(due).toISOString(),
        });
      } else {
        await tpApi.convertIssueToTodo(issueId, {
          dueAt: due ? new Date(due).toISOString() : null,
        });
      }
      onConverted();
    } finally {
      setPending(false);
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: "absolute",
        inset: 0,
        background: "rgba(20,24,28,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 80,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(420px, 92%)",
          background: DC.surface,
          borderRadius: 12,
          padding: 22,
          boxShadow: "var(--bsp-shadow-pop)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
          <div
            style={{
              font: "700 14px/1 var(--bsp-font-display)",
              textTransform: "uppercase",
              letterSpacing: "0.02em",
              flex: 1,
            }}
          >
            {mode === "rock" ? "Convert to rock" : "Convert to to-do"}
          </div>
          <button type="button" className="tp-icon-action" onClick={onClose} aria-label="Close">
            <X size={14} />
          </button>
        </div>
        {mode === "rock" ? (
          <div style={{ display: "flex", gap: 12, marginBottom: 14 }}>
            <div style={{ flex: 1 }}>
              <Label>Quarter</Label>
              <select
                className="bsp-select"
                value={quarter}
                onChange={(e) => setQuarter(e.target.value)}
              >
                {(() => {
                  const list: string[] = [];
                  let q = currentQuarter();
                  for (let i = 0; i < 4; i++) {
                    list.push(q);
                    q = nextQuarter(q);
                  }
                  return list.map((q) => (
                    <option key={q} value={q}>{q}</option>
                  ));
                })()}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <Label>Due</Label>
              <input
                type="date"
                className="bsp-input"
                value={due}
                onChange={(e) => setDue(e.target.value)}
              />
            </div>
          </div>
        ) : (
          <div style={{ marginBottom: 14 }}>
            <Label>Due date (optional)</Label>
            <input
              type="date"
              className="bsp-input"
              value={due}
              onChange={(e) => setDue(e.target.value)}
            />
          </div>
        )}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
          <button type="button" className="bsp-btn bsp-btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="bsp-btn bsp-btn-primary"
            disabled={pending}
            onClick={go}
          >
            {pending ? "Converting…" : "Convert"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Block({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section
      style={{
        background: DC.surface,
        border: `1px solid ${DC.border}`,
        borderRadius: 8,
        padding: 14,
      }}
    >
      <div
        style={{
          font: "700 10px/1 var(--bsp-font-sans)",
          letterSpacing: "0.14em",
          textTransform: "uppercase",
          color: DC.grey,
          marginBottom: 10,
        }}
      >
        {title}
      </div>
      {children}
    </section>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        font: "700 10px/1 var(--bsp-font-sans)",
        letterSpacing: "0.10em",
        textTransform: "uppercase",
        color: DC.grey,
        marginBottom: 6,
      }}
    >
      {children}
    </div>
  );
}
